<template>
  <div class="min-h-screen flex items-center justify-center bg-base-200">
    <div class="text-center max-w-md mx-auto px-4">
      <!-- Error Icon -->
      <div class="text-8xl mb-6">
        <span v-if="error.statusCode === 404">🔍</span>
        <span v-else-if="error.statusCode >= 500">🚧</span>
        <span v-else>⚠️</span>
      </div>

      <!-- Error Title -->
      <h1 class="text-4xl font-bold text-base-content mb-4">
        {{ getErrorTitle() }}
      </h1>

      <!-- Error Message -->
      <p class="text-lg text-base-content/70 mb-8">
        {{ getErrorMessage() }}
      </p>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <button @click="handleError" class="btn btn-primary">
          Intentar de nuevo
        </button>

        <NuxtLink to="/" class="btn btn-outline"> Ir al inicio </NuxtLink>

        <NuxtLink v-if="isBlogError" to="/blog" class="btn btn-ghost">
          Volver al Blog
        </NuxtLink>
      </div>

      <!-- Error Details (only in development) -->
      <details v-if="isDev && error.stack" class="mt-8 text-left">
        <summary class="cursor-pointer text-sm text-base-content/60 mb-2">
          Detalles técnicos (solo en desarrollo)
        </summary>
        <pre class="bg-base-200 p-4 rounded-lg text-xs overflow-auto">{{
          error.stack
        }}</pre>
      </details>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  error: Object,
});

const isDev = process.dev;
const route = useRoute();

const isBlogError = computed(() => {
  return route.path.startsWith("/blog");
});

const getErrorTitle = () => {
  switch (props.error.statusCode) {
    case 404:
      return isBlogError.value
        ? "Artículo no encontrado"
        : "Página no encontrada";
    case 500:
      return "Error del servidor";
    case 503:
      return "Servicio no disponible";
    default:
      return "Algo salió mal";
  }
};

const getErrorMessage = () => {
  switch (props.error.statusCode) {
    case 404:
      if (isBlogError.value) {
        return "El artículo que buscas no existe o ha sido movido. Verifica la URL o explora otros artículos.";
      }
      return "La página que buscas no existe. Verifica la URL o navega a otra sección.";
    case 500:
      return "Estamos experimentando problemas técnicos. Por favor, inténtalo de nuevo en unos momentos.";
    case 503:
      return "El servicio está temporalmente no disponible. Vuelve a intentarlo más tarde.";
    default:
      return (
        props.error.statusMessage ||
        "Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo."
      );
  }
};

const handleError = async () => {
  try {
    if (isBlogError.value) {
      // For blog errors, try to refresh the page data
      await navigateTo("/blog", { replace: true });
    } else {
      // For other errors, clear and reload
      await clearError({ redirect: route.path });
    }
  } catch (err) {
    // If clearing error fails, redirect to home
    await navigateTo("/", { replace: true });
  }
};

// SEO
useHead({
  title: computed(() => `Error ${props.error.statusCode} - Soleando`),
  meta: [{ name: "robots", content: "noindex" }],
});
</script>
